import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewChild,
    inject
} from "@angular/core";
import { ButtonComponent } from "@components/button/button.component";
import { MapDrawComponent } from "@components/map/components/menu/map-draw/map-draw.component";
import { MapLayersComponent } from "@components/map/components/menu/map-layers/map-layers.component";
import { MapComponent } from "@components/map/map.component";
import { TogglePanelComponent } from "@components/toggle-panel/toggle-panel.component";
import _ from "lodash";
import { Subject } from "rxjs";
import { IMapComponent } from "~app/common/components/map/map.models";
import {
    IFilterParams,
    IMapDrawLayer,
    IMapGeometry,
    IMapGeometryWicket,
    IMapSettings
} from "~app/common/components/map/map.settings";
import { IMapQueryResult } from "~app/common/components/map/services/map-query.service";
import { ToastService } from "~app/common/components/toast/toast.service";
import { IBridgeEvent } from "~app/common/services/event-bridge.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { DsoEventService } from "~app/features/transport/destineringsomraden/services/dso-event.service";
import { DsoCacheService } from "../dso-edit/dso-cache.service";
import { DsoEvents } from "../models/dso-events";
import { Destineringsomrade, PropertiesChangedEvent } from "../models/dso-models";
import { DsoService } from "../services/dso.service";
import { DsoKartaTexts } from "./dso-karta-texts";

@Component({
    selector: "rask-dso-karta",
    templateUrl: "./dso-karta.component.html",
    styleUrls: ["./dso-karta.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MapComponent,
        MapDrawComponent,
        MapLayersComponent,
        TogglePanelComponent,
        ButtonComponent
    ]
})
export class DsoKartaComponent implements OnInit, OnDestroy {
    @ViewChild("attributeswindow", { static: true }) attributeswindow: ElementRef;
    current: Destineringsomrade;
    featureLayers: IMapQueryResult[];
    featuregeometry: IMapGeometry;
    filter = new Subject<IFilterParams>();
    @ViewChild("importwindow", { static: true }) importwindow: ElementRef;
    @ViewChild("infowindow", { static: true }) infowindow: ElementRef;
    map: IMapComponent;
    mapsettings: IMapSettings;
    omraden: Destineringsomrade[];
    selectedOmrade: Destineringsomrade;

    private readonly cacheService = inject(DsoCacheService);
    private readonly dsoEvents: IBridgeEvent[] = [];
    private readonly dsoService = inject(DsoService);
    private readonly eventService = inject(DsoEventService);
    private readonly layerId = "omraden";
    private readonly renderer = inject(Renderer2);
    private readonly snapLayerId = "destineringoutline";
    private readonly toastService = inject(ToastService);

    private isDirty: boolean;

    canCreate(): boolean {
        if (this.isDirty) {
            this.toastService.add(DsoKartaTexts.texts.dirtyPrompt, 3);
        } else {
            this.loadOutlineGeometry();
        }
        return !this.isDirty;
    }

    clickFeatureImport(geometry: IMapGeometry) {
        this.closeAllPopups();
        const imp = ObjectUtils.deepCopy(geometry);
        imp.color = [
            100,
            255,
            100,
            0.5
        ];
        imp.visible = true;
        imp.label = this.getFeatureName("", geometry);
        this.getOmradenLayer().geometries.push(imp);
        this.refreshOmradenGeometries();
    }

    clickFeatureInfo($event, geometry: IMapGeometry) {
        this.featuregeometry = geometry;
        const style = window.getComputedStyle(this.importwindow.nativeElement, null);
        const parentY = parseInt(style.top.replace("px", ""), 10);
        const parentX = parseInt(style.left.replace("px", ""), 10);
        const parentWidth = this.importwindow.nativeElement.clientWidth;
        this.setElementPos(this.attributeswindow.nativeElement, parentWidth + parentX + 20, parentY);
    }

    clickPolygonEdit(mouse, geometry) {
        this.closeAllPopups();
        if (ObjectUtils.isNullOrUndefined(geometry)) {
            return;
        }
        const layer = this.getOmradenLayer();
        this.map.getSketch().edit(layer, geometry, (g) => this.edited(g), true);
        this.loadOutlineGeometry(geometry?.tag?.id);
    }

    deleteDestineringsomrade(guids: string[]) {
        const layer = this.getOmradenLayer();
        _.remove(layer.geometries, geo => guids.some(g => g === geo.tag?.guid));
        this.refreshOmradenGeometries();
    }

    deleteKartgeometries(geometries: IMapGeometry[]): boolean {
        this.eventService.emit(DsoEvents.geometriesDeleted, geometries);
        return true;
    }

    edited(geometry: IMapGeometryWicket) {
        const layer = this.getOmradenLayer();
        const original = layer.geometries.find(x => x.id === geometry.id) as IMapGeometryWicket;
        if (ObjectUtils.isNullOrUndefined(original)) {
            // new area
            geometry.color = this.dsoService.getRandomColor();
            layer.geometries.push(geometry);
            this.eventService.emit(DsoEvents.geometryNewCreated, geometry);
        } else {
            // update area
            original.wkt = geometry.wkt;
            this.eventService.emit(DsoEvents.geometryModified, geometry);
        }
        this.refreshOmradenGeometries();
    }

    geometryCreated(geometry: IMapGeometry) {
        this.eventService.emit(DsoEvents.geometryNewCreated, geometry);
    }

    getAttributes(geometry: IMapGeometry): any[] {
        const result = [];
        const keys = Object.keys(geometry?.attributes);
        keys.forEach(key => {
            result.push({
                key: key,
                value: geometry?.attributes[key]
            });
        });
        return result;
    }

    getFeatureName(sublayerid: string, geometry: IMapGeometry) {
        if (ObjectUtils.isNullOrUndefined(geometry)) {
            return undefined;
        }
        switch (sublayerid) {
            case "LKF":
                return geometry?.attributes?.FORSAMNAMN;
            case "LKD":
                return geometry?.attributes?.DISTRNAMN;
            default:
                return geometry?.attributes?.NAMN ? geometry?.attributes?.NAMN : "Geometri";
        }
    }

    initEventListeners() {
        this.dsoEvents.push(this.eventService.subscribe(DsoEvents.destineringsomradenLoaded, (payload: Destineringsomrade[]) => {
            this.refreshOmradenGeometries();
            this.omraden = payload;
            this.filterDestineringsomraden();
        }));

        this.dsoEvents.push(this.eventService.subscribe(DsoEvents.destineringsomradeFormStateChanged, (dirty: boolean) => {
            this.isDirty = dirty;
        }));

        this.dsoEvents.push(this.eventService.subscribe(DsoEvents.destineringsomradePropertiesChanged, (event: PropertiesChangedEvent) => {
            const layer = this.getOmradenLayer();
            const geometry = layer.geometries.find(x => x.id === event.guid) as IMapGeometryWicket;
            if (ObjectUtils.hasValue(geometry)) {
                geometry.color = this.dsoService.hexToRgb(event.color);
                geometry.label = event.name;
                this.refreshOmradenGeometries();
            }
        }));

        this.dsoEvents.push(this.eventService.subscribe(DsoEvents.dsoSelected, (payload: Destineringsomrade) => {
            this.current = payload;
            if (ObjectUtils.hasValue(this.current)) {
                const geometry = this.mapToGeometry(this.current);
                this.getOmradenLayer().geometries = [geometry];
                this.refreshOmradenGeometries();
                this.filterDestineringsomraden();
            } else {
                this.getOmradenLayer().geometries = [];
                this.refreshOmradenGeometries();
                this.filterDestineringsomraden();
            }
        }));
        this.dsoEvents.push(this.eventService.subscribe(DsoEvents.dsoRemoveOutline, (param: any) => {
            this.removeOutlineGeometry();
        }));
    }

    loadOutlineGeometry(hideGeometry?: number) {
        if (!this.map || !this.cacheService?.getAll()) {
            return;
        }
        const geometrier = [];
        this.cacheService.getAll().filter(omr => (omr.id !== hideGeometry)).forEach(omr => {
            geometrier.push({
                id: `outline${omr.id}`,
                wkt: omr.geometri,
                visible: true,
                color: [
                    0,
                    0,
                    0,
                    0
                ],
                tag: omr,
                label: omr.namn
            });
        });
        this.map.setGeometries(this.snapLayerId, geometrier);
    }

    ngOnDestroy(): void {
        this.eventService.unsubscribeMany(this.dsoEvents);
        this.dsoEvents.splice(0);
    }

    ngOnInit(): void {
        this.mapsettings = {
            id: "dso",
            coords: true,
            zoomButtons: true,
            onMapReady: (map) => this.onMapReady(map),
            onLayerReady: (map, layer) => this.onLayerReady(map, layer),
            onMapClick: (mouse, hitlist) => this.onMapClick(mouse, hitlist),
            featurelayers: [
                {
                    id: "VerkGemAdminGrans",
                    visible: true
                },
                {
                    id: "VerkGemLLD",
                    visible: true
                },
                {
                    id: "VerkGemLKF",
                    visible: true
                },
                {
                    id: "VerkGemFastigheter",
                    visible: true
                },
                {
                    id: "Vagar",
                    visible: true
                },
                {
                    id: "VFS_PlaneradVag",
                    visible: true
                },
                {
                    id: "Destineringsomraden",
                    visible: true,
                    onClickFeature: (geometries) => {
                        this.clickDestineringsomrade(geometries);
                        return false;
                    }
                }
            ],
            drawLayers: [
                {
                    id: "mottagningsplatser",
                    title: "Mottagningsplatser",
                    showGeometriesMenu: false,
                    visible: true,
                    readonly: true,
                    geometries: [],
                    selectable: false,
                    onHoverIn: ((g, m) => { this.showInfowindow(m.x, m.y, g); }),
                    onHoverOut: ((g, m) => { this.hideInfowindow(); })
                },
                {
                    id: this.layerId,
                    title: "Områden",
                    visible: true,
                    readonly: true,
                    geometries: [],
                    hideInLayerList: true,
                    selectable: true,
                    onClick: (m, g) => {
                        this.clickPolygonEdit(m, g);
                        return false;
                    },
                    onGeometryDeleted: (geometries) => this.deleteKartgeometries(geometries),
                    onGeometryCanceled: () => this.removeOutlineGeometry(),
                    showGeometriesMenu: true,
                    drawOptions: [
                        {
                            title: `Skapa nytt område`,
                            type: "polygon",
                            callback: (g) => this.geometryCreated(g),
                            onBeforeDraw: () => this.canCreate(),
                            snapLayerId: this.snapLayerId
                        }
                    ]
                },
                {
                    id: this.snapLayerId,
                    title: "DestineringOutline",
                    showGeometriesMenu: false,
                    visible: true,
                    readonly: true,
                    geometries: [],
                    selectable: false,
                    hideInLayerList: true
                }
            ],
            icons: [
                {
                    id: "fabrik",
                    color: "black",
                    size: 12,
                    outlineWidth: 0,
                    arcgisStyle: "path",
                    outlineColor: "gray",
                    iconSvgPath: "M475.115 163.781L336 252.309v-68.28c0-18.916-20.931-30.399-36.885-20.248L160 252.309V56c0-13.255-10.745-24-24-24H24C10.745 32 0 42.745 0 56v400c0 13.255 10.745 24 24 24h464c13.255 0 24-10.745 24-24V184.029c0-18.917-20.931-30.399-36.885-20.248z"
                }
            ]
        };
        this.initEventListeners();
    }

    onLayerReady(map: IMapComponent, layer: string) {
        switch (layer) {
            case "mottagningsplatser":
                this.loadMottagningsplatser();
                break;
            case "Destineringsomraden":

                // todo: make default filter on IMapLayer to avoid inital flickering
                this.filterDestineringsomraden();
                break;
        }
    }

    onMapClick(mouse, hitlist) {
        if (this.map.isWidgetsActive(["map-layers"])) {
            this.map.closeWidgets();
        }
    }

    onMapReady(map: IMapComponent) {
        this.map = map;
    }

    removeOutlineGeometry() {
        this.map?.clearGeometries(this.snapLayerId);
    }

    private clickAdministrativgrans(layers: IMapQueryResult[], mouse) {
        this.closeAllPopups();
        // eslint-disable-next-line sonarjs/no-inverted-boolean-check
        if (!(layers?.length > 0) || !layers.some(g => g.geometries?.length > 0)) {
            return;
        }
        this.featureLayers = layers;
        this.setElementPos(this.importwindow.nativeElement, mouse.x, mouse.y);
    }

    private clickDestineringsomrade(result: IMapQueryResult[]) {
        this.closeAllPopups();
        if (this.isDirty) {
            this.map.gotoAll([this.layerId]);
            return;
        }
        if (result?.length > 0 && result.some(r => r.geometries?.length > 0)) {
            const guid = this.getGuidFromFeatureGeometry(result[0]?.geometries[0]);
            this.cacheService.setSelected(guid);
        } else {
            this.cacheService.setSelected(null);
        }
    }

    private closeAllPopups() {
        this.renderer.setStyle(this.importwindow.nativeElement, "display", "none");
        this.renderer.setStyle(this.infowindow.nativeElement, "display", "none");
        this.renderer.setStyle(this.attributeswindow.nativeElement, "display", "none");
    }

    private filterDestineringsomraden() {
        const guids = this.omraden?.map(o => o.guid);
        const exclude = this.current?.guid;
        if (guids?.length > 0) {
            const exclusiveGUIDS = ObjectUtils.isNullOrUndefined(exclude) ? guids : guids.filter(x => x.toLowerCase() !== exclude.toLowerCase());
            if (exclusiveGUIDS?.length > 0) {
                const filterGUIDS = exclusiveGUIDS.map(guid => `GUID = '${guid.toUpperCase()}'`);
                const filter = filterGUIDS.join(" OR ");
                this.map?.filterLayer("Destineringsomraden", filter, ["Destineringsområdeytor"]);
                return;
            }
        } else {
            // show none
            this.map?.filterLayer("Destineringsomraden", "GUID = 'null'", ["Destineringsområdeytor"]);
        }
    }

    private getGuidFromFeatureGeometry(geometry: IMapGeometry) {
        return geometry?.attributes?.GUID?.replace("{", "").replace("}", "");
    }

    private getOmradenLayer(): IMapDrawLayer {
        return this.mapsettings.drawLayers.find(x => x.id === this.layerId);
    }

    private hideInfowindow() {
        const element = this.infowindow.nativeElement;
        this.clearInfowindow();
        this.renderer.setStyle(element, "display", "none");
    }

    private loadMottagningsplatser() {
        this.dsoService.getMottagningsplatser().subscribe(mottagningsplatser => {
            const geometries = mottagningsplatser.map(mpl => ({
                id: mpl.id,
                label: mpl.namn,
                easting: mpl.easting,
                northing: mpl.northing,
                iconid: "fabrik",
                visible: true
            }));
            this.mapsettings.drawLayers.find(x => x.id === "mottagningsplatser").geometries = geometries;
            this.map.refreshGeometries("mottagningsplatser");
        });
    }

    private mapToGeometry(o: Destineringsomrade): IMapGeometryWicket {
        return {
            id: o.guid,
            wkt: o.geometri,
            color: this.dsoService.hexToRgb(o.color, 0.2),
            visible: true,
            label: o.namn,
            tag: o
        };
    }

    private refreshOmradenGeometries() {
        this.map?.refreshGeometries(this.layerId);
    }

    private setElementPos(el, x, y) {
        this.renderer.setStyle(el, "top", `${y}px`);
        this.renderer.setStyle(el, "left", `${x}px`);
        this.renderer.setStyle(el, "display", "block");
    }

    private clearInfowindow(): void {
        const element = this.infowindow.nativeElement;
        while (element.firstChild) {
            this.renderer.removeChild(element, element.firstChild);
        }
    }

    private showInfowindow(x: number, y: number, info: IMapGeometry) {
        const element = this.infowindow.nativeElement;
        this.clearInfowindow();
        this.setElementPos(element, x, y);
        const span = this.renderer.createElement("span");
        this.renderer.appendChild(span, this.renderer.createText(info.label));
        this.renderer.appendChild(element, span);
    }
}
